import fs from 'node:fs'
import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import type { Schema } from '@core-engine/schema/types.js'
import { ensureSnapshotGitFiles } from '@core-engine/snapshots/gitignore.js'
import { writeProjectFile } from '@core-engine/snapshots/merge.js'
import {
  BARREL_SCOPE,
  createManifest,
  getCurrentCliVersion,
  saveManifest,
  writeSnapshotFiles
} from '@core-engine/snapshots/store.js'
import { getGeneratedFilePaths } from '@core-engine/utils/generated-files.js'
import { runSinglePipeline } from '@next-adapter/generators/index.js'
import { defaultSettingsSchema } from '@next-adapter/init/templates/core/default-settings.js'
import { ensureCoreSchemaOwnership } from '@next-adapter/preset-runtime.js'
import { renderBarrelFiles } from '@next-adapter/snapshots/barrels.js'

export interface CoreSchemasScaffoldOptions {
  cwd: string
  config: ResolvedBetterstartConfig
}

export interface CoreSchemasScaffoldResult {
  schemas: string[]
  generatedFiles: string[]
  errors: string[]
}

export function scaffoldCoreSchemas({
  cwd,
  config
}: CoreSchemasScaffoldOptions): CoreSchemasScaffoldResult {
  const result: CoreSchemasScaffoldResult = {
    schemas: [],
    generatedFiles: [],
    errors: []
  }

  const schemaContent = defaultSettingsSchema()
  const schemaName = 'settings'
  const schemaPath = path.join(cwd, config.paths.schemas, `${schemaName}.json`)
  fs.mkdirSync(path.dirname(schemaPath), { recursive: true })
  fs.writeFileSync(schemaPath, schemaContent, 'utf-8')
  result.schemas.push(`${schemaName}.json`)

  ensureSnapshotGitFiles(cwd)

  try {
    const parsed = JSON.parse(schemaContent) as Schema
    const cliVersion = getCurrentCliVersion()
    const pipelineResult = runSinglePipeline(parsed, cwd, config, {
      force: true,
      silent: true,
      mode: 'init'
    })

    for (const file of pipelineResult.files) {
      writeProjectFile(cwd, file)
    }

    writeSnapshotFiles(cwd, parsed.name, pipelineResult.files)
    saveManifest(
      cwd,
      parsed.name,
      createManifest(parsed.name, parsed, cliVersion, pipelineResult.files, [])
    )
    ensureCoreSchemaOwnership(cwd, parsed.name)
    result.generatedFiles.push(...getGeneratedFilePaths(pipelineResult.files))

    if (!pipelineResult.success) {
      result.errors.push(...pipelineResult.errors.map((error) => `${schemaName}.json: ${error}`))
    }

    const barrelFiles = renderBarrelFiles(cwd, config)
    for (const file of barrelFiles) {
      writeProjectFile(cwd, file)
    }
    writeSnapshotFiles(cwd, BARREL_SCOPE, barrelFiles)
    saveManifest(
      cwd,
      BARREL_SCOPE,
      createManifest(BARREL_SCOPE, { name: BARREL_SCOPE }, cliVersion, barrelFiles, [])
    )
    result.generatedFiles.push(...getGeneratedFilePaths(barrelFiles))
  } catch (error) {
    result.errors.push(
      `${schemaName}.json: ${error instanceof Error ? error.message : String(error)}`
    )
  }

  return result
}
